import React, { useState } from 'react';
import { Calculator, DollarSign, PieChart } from 'lucide-react';

interface BudgetItem {
  category: string;
  amount: number;
}

const BudgetPlannerPage: React.FC = () => {
  const [budgetItems, setBudgetItems] = useState<BudgetItem[]>([]);
  const [category, setCategory] = useState('');
  const [amount, setAmount] = useState('');

  const handleAddItem = (e: React.FormEvent) => {
    e.preventDefault();
    if (category && amount) {
      setBudgetItems([...budgetItems, {
        category,
        amount: parseFloat(amount)
      }]);
      setCategory('');
      setAmount('');
    }
  };

  const totalBudget = budgetItems.reduce((sum, item) => sum + item.amount, 0);

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold mb-4 flex items-center justify-center gap-2">
          <Calculator className="h-8 w-8" />
          Budget Planner
        </h1>
        <p className="text-muted-foreground">
          Plan and track your property investment budget effectively
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <div className="bg-card rounded-lg p-6 shadow-lg">
          <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
            <DollarSign className="h-6 w-6" />
            Add Budget Item
          </h2>
          <form onSubmit={handleAddItem} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Category</label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full p-2 rounded border bg-background"
                placeholder="e.g., Down Payment"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Amount ($)</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full p-2 rounded border bg-background"
                placeholder="0.00"
                min="0"
                step="0.01"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full bg-primary text-primary-foreground py-2 rounded hover:bg-primary/90 transition"
            >
              Add Item
            </button>
          </form>
        </div>

        <div className="bg-card rounded-lg p-6 shadow-lg">
          <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
            <PieChart className="h-6 w-6" />
            Budget Summary
          </h2>
          <div className="space-y-4">
            <div className="border-b pb-4">
              <p className="text-lg font-medium">Total Budget</p>
              <p className="text-3xl font-bold">${totalBudget.toLocaleString()}</p>
            </div>
            <div className="space-y-2">
              {budgetItems.map((item, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="font-medium">{item.category}</span>
                  <span>${item.amount.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetPlannerPage;